import {
  SAV_ATTACHMENT_MAX_BYTES,
  SAV_KIND_VALUES,
  savKindOf,
  savReasonsFor,
  savTicketSchema,
  type SavTicketInput,
} from "@erp/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, Plus, Trash2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { AsyncPicker } from "~/components/ui/async-picker";
import { Button } from "~/components/ui/button";
import { Field, FieldGroup } from "~/components/ui/field";
import { Input, Textarea } from "~/components/ui/input";
import { Page, PageHeader, Panel } from "~/components/ui/page";
import { Select } from "~/components/ui/select";
import { ErrorState, Skeleton } from "~/components/ui/states";
import {
  CustomerItem,
  searchCustomers,
  searchVariants,
  variantLabel,
  VariantItem,
  type CustomerOption,
  type VariantOption,
} from "~/features/sales/pickers";
import { api, unwrap } from "~/lib/api";
import { cn } from "~/lib/cn";
import { errorMessageKey, fieldErrors } from "~/lib/errors";
import { errorAt, schemaErrors, type ErrorMap } from "~/lib/forms";
import { useFormat, useT } from "~/lib/i18n";
import { readBase64 } from "~/lib/files";
import { useSavReasons } from "~/features/sav/reasons";

const search = z.object({ orderId: z.uuid().optional(), customerId: z.uuid().optional() });

export const Route = createFileRoute("/_app/sav/new")({ validateSearch: search, component: NewSav });

const fetchOrder = (id: string) => unwrap(api.orders({ id }).get());
type Order = Awaited<ReturnType<typeof fetchOrder>>;
type Reason = string;
type Kind = (typeof SAV_KIND_VALUES)[number];

/** Orders found by number, external reference or customer name (every view, archived ones included). */
async function searchOrders(q: string) {
  const page = await unwrap(api.orders.get({ query: { q: q || undefined, view: "all", offset: 0, limit: 20 } }));
  return page.items;
}
type OrderOption = Awaited<ReturnType<typeof searchOrders>>[number];

function OrderItem({ order }: { order: OrderOption }) {
  const t = useT();
  const format = useFormat();
  return (
    <div className="flex min-w-0 flex-1 items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate font-medium">{order.customerName}</p>
        <p className="truncate text-xs text-fg-3 tabular">
          {[order.number, order.externalReference].filter(Boolean).join(" — ")}
        </p>
      </div>
      <div className="shrink-0 text-right text-xs text-fg-3">
        <p className="tabular">{format.date(order.orderedAt, "short")}</p>
        <p>{t(`orders.statuses.${order.status}`)}</p>
      </div>
    </div>
  );
}

const WHOLE_SET = "whole";

interface OrderPick {
  checked: boolean;
  quantity: string;
  /** On a line still sold as a kit: the one article concerned, or null for the whole set. */
  article?: string | null;
}
interface FreeLine {
  key: string;
  variant: VariantOption | null;
  label: string;
  quantity: string;
}
interface Header {
  /** Chosen first: it narrows the reasons offered. */
  kind: Kind | null;
  reason: Reason | null;
  /** The person concerned in a grouped order, when not the order's customer. */
  contactName: string;
  subject: string;
  description: string;
}

function BackLink() {
  const t = useT();
  return (
    <Link to="/sav" className="mb-2 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg">
      <ChevronLeft className="size-4" aria-hidden />
      {t("sav.title")}
    </Link>
  );
}

function NewSav() {
  const { orderId } = Route.useSearch();
  const order = useQuery({ queryKey: ["orders", orderId], queryFn: () => fetchOrder(orderId!), enabled: Boolean(orderId) });
  // The header (kind, reason, subject…) and photos live here so picking an order — which reloads the
  // order query and remounts the form — does not reset what the user already filled in.
  const [header, setHeader] = useState<Header>({ kind: null, reason: null, contactName: "", subject: "", description: "" });
  const [files, setFiles] = useState<File[]>([]);
  if (orderId && order.isPending)
    return (
      <Page>
        <BackLink />
        <Skeleton className="h-8 w-60" />
        <Skeleton className="mt-6 h-96 w-full" />
      </Page>
    );
  if (orderId && order.isError)
    return (
      <Page>
        <BackLink />
        <ErrorState error={order.error} onRetry={() => void order.refetch()} />
      </Page>
    );
  return (
    <SavForm
      key={order.data?.id ?? "none"}
      order={order.data ?? null}
      header={header}
      setHeader={setHeader}
      files={files}
      setFiles={setFiles}
    />
  );
}

/** Switches between a case opened from an order and one opened from a customer record. */
function ModeLink({ onClick, children }: { onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="min-h-11 self-start text-sm text-fg-2 underline underline-offset-4 hover:text-fg"
    >
      {children}
    </button>
  );
}

const newLine = (): FreeLine => ({ key: crypto.randomUUID(), variant: null, label: "", quantity: "1" });

function SavForm({
  order,
  header,
  setHeader,
  files,
  setFiles,
}: {
  order: Order | null;
  header: Header;
  setHeader: (header: Header) => void;
  files: File[];
  setFiles: (files: File[]) => void;
}) {
  const t = useT();
  const format = useFormat();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const reasons = useSavReasons();
  const kind = header.kind ?? (header.reason ? savKindOf(header.reason, reasons.custom) : null);
  const pickOrder = (picked: OrderOption | null) =>
    void navigate({ to: "/sav/new", search: picked ? { orderId: picked.id } : {}, replace: true });
  const [customer, setCustomer] = useState<CustomerOption | null>(null);
  // Cases start from their order; one without (an old sale, a gift) names its customer record instead.
  const [withoutOrder, setWithoutOrder] = useState(false);
  const [picks, setPicks] = useState<Record<string, OrderPick>>(() =>
    Object.fromEntries(
      (order?.lines ?? []).map((l) => [
        l.id,
        { checked: order?.lines.length === 1, quantity: String(Math.max(1, l.shippedQuantity || l.quantity)) },
      ]),
    ),
  );
  const [freeLines, setFreeLines] = useState<FreeLine[]>([newLine()]);
  const [submitted, setSubmitted] = useState(false);
  const [serverErrors, setServerErrors] = useState<ErrorMap>({});

  const toPayload = (): SavTicketInput => ({
    orderId: order?.id ?? null,
    customerId: order ? null : (customer?.id ?? null),
    kind,
    contactName: header.contactName,
    reason: header.reason ?? ("" as Reason),
    subject: header.subject,
    description: header.description,
    lines: order
      ? order.lines
          .filter((l) => picks[l.id]?.checked)
          .map((l) => {
            const article = l.articles.find((a) => a.variantId === picks[l.id]!.article);
            return {
              orderLineId: l.id,
              label: article?.label ?? l.label,
              sku: article?.sku ?? l.sku,
              variantId: article?.variantId ?? l.variantId,
              quantity: picks[l.id]!.quantity,
            };
          })
      : freeLines
          .filter((l) => l.label.trim() || l.variant)
          .map((l) => ({
            variantId: l.variant?.variantId ?? null,
            sku: l.variant?.sku ?? null,
            label: l.label.trim() || (l.variant ? variantLabel(l.variant) : ""),
            quantity: l.quantity,
          })),
  });
  const localErrors = submitted ? schemaErrors(savTicketSchema, toPayload()) : {};
  const customerMissing = submitted && !order && withoutOrder && !customer;
  const orderMissing = submitted && !order && !withoutOrder;
  const errors = { ...serverErrors, ...localErrors };

  const create = useMutation({
    mutationFn: async () => {
      const ticket = await unwrap(api.sav.post(savTicketSchema.parse(toPayload())));
      // Photos picked with the request join the case right after it is opened.
      for (const file of files)
        await unwrap(
          api.sav({ id: ticket.id }).attachments.post({
            name: file.name,
            type: file.type || "application/octet-stream",
            dataBase64: await readBase64(file),
          }),
        );
      return ticket;
    },
    onSuccess: (ticket) => {
      void queryClient.invalidateQueries({ queryKey: ["sav"] });
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success(t("sav.created"));
      void navigate({ to: "/sav/$id", params: { id: ticket.id } });
    },
    onError: (error) => {
      setServerErrors(fieldErrors(error));
      toast.error(t(errorMessageKey(error)));
    },
  });

  const submit = () => {
    setSubmitted(true);
    if (Object.keys(schemaErrors(savTicketSchema, toPayload())).length || (!order && (!withoutOrder || !customer))) return;
    create.mutate();
  };

  const updateLine = (key: string, patch: Partial<FreeLine>) =>
    setFreeLines((lines) => lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  return (
    <Page>
      <PageHeader
        back={<BackLink />}
        title={t("sav.new")}
        meta={
          order ? (
            <span className="flex flex-wrap items-center gap-2">
              <span className="tabular">
                {t("sav.fields.order")} {order.number} — {order.party.name}
              </span>
              <Button variant="ghost" size="sm" onClick={() => pickOrder(null)}>
                <X />
                {t("sav.actions.changeOrder")}
              </Button>
            </span>
          ) : undefined
        }
        actions={
          <Button variant="primary" onClick={submit} loading={create.isPending}>
            {t("sav.actions.create")}
          </Button>
        }
      />
      <div className="grid grid-cols-[minmax(0,1fr)] gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <Panel className="p-5 md:p-6">
          <FieldGroup>
            <Field
              label={t("sav.fields.kind")}
              hint={kind ? t(`sav.kindHints.${kind}`) : undefined}
              error={errorAt(t, errors, "kind")}
            >
              <Select
                value={header.kind}
                onValueChange={(next) =>
                  setHeader({
                    ...header,
                    kind: next,
                    // A reason of another type no longer fits.
                    reason: header.reason && savReasonsFor(next, reasons.custom).includes(header.reason) ? header.reason : null,
                  })
                }
                placeholder={t("sav.pickKind")}
                options={SAV_KIND_VALUES.map((k) => ({ value: k, label: t(`sav.kinds.${k}`) }))}
              />
            </Field>
            {order ? null : withoutOrder ? (
              <div className="flex flex-col gap-2">
                <Field label={t("sav.fields.customer")} error={customerMissing ? t("errors.field.required") : undefined}>
                  <AsyncPicker
                    queryKey="customers"
                    search={searchCustomers}
                    value={customer}
                    onChange={setCustomer}
                    itemKey={(c) => c.id}
                    itemLabel={(c) => c.displayName}
                    renderItem={(c) => <CustomerItem customer={c} />}
                    placeholder={t("sav.pickCustomer")}
                  />
                </Field>
                <ModeLink onClick={() => setWithoutOrder(false)}>{t("sav.actions.withOrder")}</ModeLink>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Field
                  label={t("sav.fields.order")}
                  hint={t("sav.hints.pickOrder")}
                  error={orderMissing ? t("errors.field.required") : undefined}
                >
                  <AsyncPicker
                    queryKey="orders"
                    search={searchOrders}
                    value={null}
                    onChange={pickOrder}
                    itemKey={(o) => o.id}
                    itemLabel={(o) => `${o.number} — ${o.customerName}`}
                    renderItem={(o) => <OrderItem order={o} />}
                    placeholder={t("sav.pickOrder")}
                  />
                </Field>
                <ModeLink onClick={() => setWithoutOrder(true)}>{t("sav.actions.withoutOrder")}</ModeLink>
              </div>
            )}
            <Field label={t("sav.fields.reason")} error={errorAt(t, errors, "reason")}>
              <Select
                value={header.reason}
                onValueChange={(reason) => setHeader({ ...header, reason })}
                placeholder={t(header.kind ? "sav.pickReason" : "sav.pickKindFirst")}
                disabled={!header.kind}
                options={
                  header.kind
                    ? savReasonsFor(header.kind, reasons.custom).map((r) => ({ value: r, label: reasons.label(r) }))
                    : []
                }
              />
            </Field>
            {order || withoutOrder ? (
              <Field
                label={t("sav.fields.contactName")}
                hint={t("sav.hints.contactName")}
                optionalLabel={t("common.states.optional")}
                error={errorAt(t, errors, "contactName")}
              >
                <Input
                  value={header.contactName}
                  onChange={(e) => setHeader({ ...header, contactName: e.target.value })}
                  placeholder={order?.party.name ?? customer?.displayName}
                />
              </Field>
            ) : null}
            <Field label={t("sav.fields.subject")} error={errorAt(t, errors, "subject")}>
              <Input value={header.subject} onChange={(e) => setHeader({ ...header, subject: e.target.value })} />
            </Field>
            <Field
              label={t("sav.fields.description")}
              optionalLabel={t("common.states.optional")}
              error={errorAt(t, errors, "description")}
            >
              <Textarea
                rows={5}
                value={header.description}
                onChange={(e) => setHeader({ ...header, description: e.target.value })}
              />
            </Field>
            <Field label={t("sav.fields.photos")} optionalLabel={t("common.states.optional")} hint={t("sav.hints.photos")}>
              <input
                type="file"
                multiple
                accept="image/*,application/pdf"
                onChange={(e) => {
                  const picked = [...(e.target.files ?? [])];
                  if (picked.some((f) => f.size > SAV_ATTACHMENT_MAX_BYTES)) {
                    toast.error(t("errors.codes.sav.attachment_too_large"));
                    return;
                  }
                  setFiles(picked);
                }}
                className="text-sm file:mr-3 file:rounded-control file:border file:border-line file:bg-surface-2 file:px-3 file:py-1.5 file:text-sm"
              />
            </Field>
          </FieldGroup>
        </Panel>

        <Panel className="p-5 md:p-6">
          <h2 className="font-display text-[15px] font-medium">{t("sav.fields.lines")}</h2>
          <p className="mt-1 mb-4 text-sm text-fg-2">
            {t(order ? "sav.hints.fromOrder" : withoutOrder ? "sav.hints.noOrder" : "sav.hints.orderFirst")}
          </p>
          {order ? (
            <ul className="flex flex-col divide-y divide-line">
              {order.lines.map((l) => {
                const pick = picks[l.id] ?? { checked: false, quantity: "1" };
                return (
                  <li key={l.id} className="flex items-center gap-3 py-2.5">
                    <label aria-label={l.label} className="flex min-h-11 min-w-0 flex-1 cursor-pointer items-center gap-3">
                      <input
                        type="checkbox"
                        checked={pick.checked}
                        onChange={(e) => setPicks({ ...picks, [l.id]: { ...pick, checked: e.target.checked } })}
                        className="size-4 shrink-0 accent-gold"
                      />
                      <span className="min-w-0">
                        <span className="block truncate text-sm">{l.label}</span>
                        <span className="block text-xs text-fg-3 tabular">
                          {[l.sku, `${format.number(l.quantity)} ×`].filter(Boolean).join(" — ")}
                        </span>
                      </span>
                    </label>
                    {l.articles.length ? (
                      <Select<string>
                        value={pick.article ?? WHOLE_SET}
                        onValueChange={(article) =>
                          setPicks({ ...picks, [l.id]: { ...pick, article: article === WHOLE_SET ? null : article } })
                        }
                        disabled={!pick.checked}
                        aria-label={t("sav.fields.article")}
                        options={[
                          { value: WHOLE_SET, label: t("sav.wholeSet") },
                          ...l.articles.map((a) => ({ value: a.variantId, label: a.label })),
                        ]}
                        className="w-48 max-sm:w-36"
                      />
                    ) : null}
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={l.quantity * (l.articles.find((a) => a.variantId === pick.article)?.quantity ?? 1)}
                      aria-label={t("sav.fields.quantity")}
                      value={pick.quantity}
                      disabled={!pick.checked}
                      onChange={(e) => setPicks({ ...picks, [l.id]: { ...pick, quantity: e.target.value } })}
                      className="w-20 text-right tabular"
                    />
                  </li>
                );
              })}
            </ul>
          ) : withoutOrder ? (
            <div className="flex flex-col gap-4">
              {freeLines.map((l, index) => (
                <div
                  key={l.key}
                  className="grid grid-cols-[minmax(0,1fr)_auto] gap-2 rounded-control border border-line p-3 sm:grid-cols-[minmax(0,1fr)_88px_auto] sm:items-start"
                >
                  <div className="col-span-2 flex min-w-0 flex-col gap-2 sm:col-span-1">
                    <AsyncPicker
                      queryKey="variants"
                      search={searchVariants}
                      value={l.variant}
                      onChange={(variant) => updateLine(l.key, { variant, label: variant ? variantLabel(variant) : l.label })}
                      itemKey={(v) => v.variantId}
                      itemLabel={(v) => `${v.sku} — ${variantLabel(v)}`}
                      renderItem={(v) => <VariantItem variant={v} />}
                      placeholder={t("sav.pickVariant")}
                      aria-label={t("sav.fields.variant")}
                    />
                    <Input
                      aria-label={t("sav.fields.label")}
                      placeholder={t("sav.fields.label")}
                      value={l.label}
                      aria-invalid={Boolean(errors[`lines.${index}.label`])}
                      onChange={(e) => updateLine(l.key, { label: e.target.value })}
                    />
                  </div>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    aria-label={t("sav.fields.quantity")}
                    value={l.quantity}
                    onChange={(e) => updateLine(l.key, { quantity: e.target.value })}
                    className="text-right tabular"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t("common.actions.remove")}
                    onClick={() => setFreeLines(freeLines.length > 1 ? freeLines.filter((x) => x.key !== l.key) : [newLine()])}
                  >
                    <Trash2 />
                  </Button>
                </div>
              ))}
              <Button variant="secondary" className={cn("self-start")} onClick={() => setFreeLines([...freeLines, newLine()])}>
                <Plus />
                {t("sav.actions.addLine")}
              </Button>
            </div>
          ) : null}
        </Panel>
      </div>
    </Page>
  );
}
